/**
 * The failure counterpart to EmptyState.
 *
 * Only an `ApiRequestError` carries a message written for a human, so that is
 * the one case whose text is shown verbatim; anything else (a thrown TypeError,
 * a rejected promise from a library) falls back to a generic sentence rather
 * than leaking a stack-trace string into the UI. The machine-readable `code` is
 * shown quietly underneath, which is what support asks for first.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { ApiRequestError } from '../../lib/apiClient';
import { Button } from './Button';
import { cn } from '../../lib/cn';

const GENERIC_MESSAGE = 'Something went wrong while loading this content. Please try again.';

function resolveMessage(error: unknown): string {
  if (error instanceof ApiRequestError && error.message.trim().length > 0) {
    return error.message;
  }
  return GENERIC_MESSAGE;
}

export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  error: unknown;
  onRetry?: () => void;
  retryLabel?: string;
}

export const ErrorState = forwardRef<HTMLDivElement, ErrorStateProps>(function ErrorState(
  { className, title = 'Something went wrong', error, onRetry, retryLabel = 'Try again', ...props },
  ref,
) {
  const message = resolveMessage(error);
  const code = error instanceof ApiRequestError ? error.code : undefined;

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
      {...props}
    >
      <span
        className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-soft text-danger-on-soft ring-8 ring-danger-soft/40"
        aria-hidden="true"
      >
        <AlertTriangle className="h-6 w-6" aria-hidden="true" />
      </span>

      <div className="max-w-md space-y-1">
        <p className="text-sm font-semibold text-content">{title}</p>
        <p className="text-sm text-content-muted">{message}</p>
        {code ? <p className="font-mono text-2xs uppercase text-content-subtle">{code}</p> : null}
      </div>

      {onRetry ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={onRetry}
          leftIcon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
          className="mt-1"
        >
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
});
